import React, { Component } from 'react';
import { Footer } from '../components/Footer';
import { MailIcon, ExternalLinkIcon, CheckCircle2Icon, CircleDashedIcon, MinusCircleIcon, ClockIcon } from 'lucide-react';
type CertStatus = 'Certified' | 'In Progress' | 'Not Pursued';
const StatusBadge = ({
  status
}: {
  status: CertStatus;
}) => {
  const styles: Record<CertStatus, {
    cls: string;
    Icon: ComponentType<any>;
  }> = {
    Certified: {
      cls: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      Icon: CheckCircle2Icon
    },
    'In Progress': {
      cls: 'bg-amber-50 text-amber-700 border-amber-200',
      Icon: CircleDashedIcon
    },
    'Not Pursued': {
      cls: 'bg-gray-100 text-gray-600 border-gray-200',
      Icon: MinusCircleIcon
    }
  };
  const {
    cls,
    Icon
  } = styles[status];
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cls}`} data-id="element-732" data-mp="1dyetkl8czvaz">
      <Icon className="w-3.5 h-3.5" strokeWidth={2} data-id="element-733" data-mp="190wj2qep180v" />
      {status}
    </span>;
};
const SectionTitle = ({
  title
}: {
  title: string;
}) => <h2 className="text-2xl md:text-3xl font-serif font-normal text-gray-600 mb-6 text-center" data-id="element-734" data-mp="1uiqeh21v6wem">
    {title}
  </h2>;
const Card = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={`bg-white/80 backdrop-blur-sm rounded-xl p-8 shadow-sm border border-gray-100 ${className}`} data-id="element-735" data-mp="1lhigkl39fxl2">
    {children}
  </div>;
export const Security = () => {
  // Certifications, only include items with explicit status. No SOC 2 / ISO 27001 claims.
  const certifications: {
    name: string;
    status: CertStatus;
    note: string;
  }[] = [{
    name: 'SOC 2 Type II',
    status: 'Not Pursued',
    note: 'Available on request once we begin a formal audit cycle. Contact security@nisa.coach for our current control summary.'
  }, {
    name: 'ISO 27001',
    status: 'Not Pursued',
    note: 'Not currently on our roadmap. Equivalent controls are documented and available for review.'
  }, {
    name: 'GDPR alignment',
    status: 'In Progress',
    note: 'We honor data subject access and deletion requests today; formal documentation is being finalized.'
  }, {
    name: 'FERPA alignment',
    status: 'In Progress',
    note: 'Nisa is designed for use under district FERPA frameworks. We sign customer DPAs as a school official under direct control.'
  }];
  const subProcessors = ['Vercel', 'AWS', 'Neon', 'Anthropic', 'OpenAI', 'Google', 'SendGrid', 'Twilio', 'Inngest', 'Langfuse', 'PostHog'];
  const documents = [{
    title: 'Privacy Policy',
    description: 'How we collect, use, and protect personal information.',
    href: '/privacy',
    external: false
  }, {
    title: 'Terms of Service',
    description: 'The terms that govern use of Nisa.',
    href: '/terms',
    external: false
  }, {
    title: 'Sub-processors list',
    description: 'View the third-party services Nisa uses and the data each handles.',
    href: '/sub-processors',
    external: false
  }];
  return <div className="flex flex-col min-h-screen" data-id="element-736" data-mp="fnhqohqibzmk">
      <main className="py-section-mobile md:py-section-desktop relative flex-grow overflow-hidden" data-id="element-737" data-mp="fosv8jrs1beq">
        {/* Background pattern */}
        <div className="absolute inset-0 bg-grid-gray-900/[0.02] -z-10" data-id="element-738" data-mp="onmpi3hmxpp4" />
        {/* XL Cyan gradient blob */}
        <div className="absolute -left-[300px] top-[10%] w-[1400px] h-[1400px] bg-gradient-to-tr from-cyan-500/15 via-primary-300/10 to-cyan-200/15 rounded-[50%_50%_45%_55%] blur-3xl -z-10" data-id="element-739" data-mp="tmpslie21lug" />
        {/* Floating gradient blobs */}
        <div className="absolute top-0 right-1/3 w-[900px] h-[900px] bg-gradient-to-br from-cyan-400/10 via-cyan-400/10 to-blue-400/10 rounded-[60%_40%_55%_45%] blur-3xl -z-10" data-id="element-740" data-mp="ybs8vzhudfq" />
        <div className="absolute top-[30%] -left-[200px] w-[800px] h-[800px] bg-gradient-to-tr from-purple-400/10 via-violet-400/10 to-fuchsia-400/10 rounded-[40%_60%_45%_55%] blur-3xl -z-10 animate-float" style={{
        animationDelay: '2s'
      }} data-id="element-741" data-mp="aon8ni0d6f3i" />
        <div className="absolute top-[60%] right-0 w-[750px] h-[750px] bg-gradient-to-bl from-amber-400/10 via-orange-400/10 to-red-400/10 rounded-[55%_45%_60%_40%] blur-3xl -z-10 animate-float" style={{
        animationDelay: '1.5s'
      }} data-id="element-742" data-mp="1ktzg3qg8wf68" />
        <div className="absolute bottom-0 left-1/4 w-[700px] h-[700px] bg-gradient-to-r from-emerald-400/10 via-green-400/10 to-cyan-400/10 rounded-[45%_55%_50%_50%] blur-3xl -z-10 animate-float" style={{
        animationDelay: '3s'
      }} data-id="element-743" data-mp="1tnbd29hiqvl1" />
        {/* Content container */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-744" data-mp="1ylw87uhwyp2m">
          {/* Hero */}
          <h1 className="text-4xl md:text-5xl font-serif font-normal text-cyan-900 mb-6 text-center" data-id="element-745" data-mp="2clfc2b3oze94">
            Security at Nisa
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto text-center leading-relaxed mb-16" data-id="element-746" data-mp="28kc03zorm2d6">
            Coaches and the teachers they support trust Nisa with sensitive
            classroom work. We treat that data with the same care, and build
            Nisa with practices that make it straightforward for your IT and
            compliance teams to review.
          </p>

          <div className="space-y-12" data-id="element-747" data-mp="82oa3h105h38">
            {/* Data Handling */}
            <section id="data-handling" data-id="element-748" data-mp="3qg47gnw70u6w">
              <SectionTitle title="Data handling" data-id="element-749" data-mp="3qf2wskj3a9pp" />
              <Card data-id="element-750" data-mp="3vfgsd33i7r6u">
                <div className="grid md:grid-cols-2 gap-6" data-id="element-751" data-mp="3veyxoawhpl11">
                  <div data-id="element-752" data-mp="3gihq7urf8qjr">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-753" data-mp="3ghy4s42kbk0z">
                      What we collect
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-754" data-mp="3lfzrr03403hq">
                      Coach and administrator account details, coaching notes
                      and artifacts uploaded by coaches, and product usage data
                      that helps us improve the experience.
                    </p>
                  </div>
                  <div data-id="element-755" data-mp="1aqkpq6kj3n3o">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-756" data-mp="1aozvki24m66c">
                      What we don't collect
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-757" data-mp="1891huwe3ivv3">
                      We do not require, request, or store student PII. We ask
                      coaches to keep student identifiers out of uploaded notes,
                      and we provide guidance in-product to support that.
                    </p>
                  </div>
                  <div data-id="element-758" data-mp="1rybu20emuyog">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-759" data-mp="1rytwxfvswb5c">
                      Where it's stored
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-760" data-mp="1i9agegvk8tdn">
                      Customer data is stored in U.S.-based managed cloud
                      services (AWS and Neon), with logical isolation per
                      organization. Access is restricted to a small set of named
                      engineers.
                    </p>
                  </div>
                  <div data-id="element-761" data-mp="25x1vttcedmj5">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-762" data-mp="2daj6m25tptrm">
                      Retention
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-763" data-mp="2ia5j29dtv5zd">
                      We retain customer data for the life of the account.
                      Customers may request export or deletion at any time; we
                      complete verified deletion requests within 30 days.
                    </p>
                  </div>
                </div>
              </Card>
            </section>

            {/* Encryption */}
            <section id="encryption" data-id="element-764" data-mp="3165clomgehl2">
              <SectionTitle title="Encryption" data-id="element-765" data-mp="35yf253jnpfmt" />
              <Card data-id="element-766" data-mp="35zqcjm7lxlmw">
                <div className="grid md:grid-cols-2 gap-6" data-id="element-767" data-mp="3ay1p44paea8t">
                  <div data-id="element-768" data-mp="3axi6smvq5ju9">
                    <h3 className="text-lg font-semibold text-gray-800 mb-1" data-id="element-769" data-mp="3b04fwqy9k7el">
                      In transit
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-770" data-mp="3jy7zvitlow4a">
                      All connections to Nisa use TLS 1.2 or higher. HTTPS is
                      enforced end-to-end across the application and APIs.
                    </p>
                  </div>
                  <div data-id="element-771" data-mp="551g9tijr1yn">
                    <h3 className="text-lg font-semibold text-gray-800 mb-1" data-id="element-772" data-mp="j2807rf0b6pg">
                      At rest
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-773" data-mp="e4okso6konvx">
                      Data at rest is encrypted with AES-256 using
                      managed-service envelope encryption across our database
                      and object storage.
                    </p>
                  </div>
                </div>
              </Card>
            </section>

            {/* Authentication & Access */}
            <section id="access" data-id="element-774" data-mp="3rgowq1vobsgo">
              <SectionTitle title="Authentication & access" data-id="element-775" data-mp="3rggia5jwkf3d" />
              <Card data-id="element-776" data-mp="33re1ezx3eq9x">
                <ul className="space-y-4" data-id="element-777" data-mp="33qm85vqyx4mt">
                  <li className="flex gap-3" data-id="element-778" data-mp="2yu47b6yrdqfr">
                    <CheckCircle2Icon className="w-5 h-5 text-cyan-700 shrink-0 mt-0.5" strokeWidth={1.75} data-id="element-779" data-mp="2ytcajstfdmnw" />
                    <div data-id="element-780" data-mp="1e9gy0dxcqjpx">
                      <span className="font-medium text-gray-800" data-id="element-781" data-mp="1j20af7kaz8t5">
                        Google OAuth
                      </span>{' '}
                      <span className="text-gray-700" data-id="element-782" data-mp="15c6k0kt5gghb">
                        for individual coach accounts, with no Nisa-managed
                        passwords by default.
                      </span>
                    </div>
                  </li>
                  <li className="flex gap-3" data-id="element-783" data-mp="2215rvyptng8z">
                    <CheckCircle2Icon className="w-5 h-5 text-cyan-700 shrink-0 mt-0.5" strokeWidth={1.75} data-id="element-784" data-mp="21zuseb3esuno" />
                    <div data-id="element-785" data-mp="1o0v0muklnzna">
                      <span className="font-medium text-gray-800" data-id="element-786" data-mp="1o3pluobftohr">
                        SAML SSO
                      </span>{' '}
                      <span className="text-gray-700" data-id="element-787" data-mp="clojnrjt8t7l">
                        available for school and district plans, with
                        IdP-managed provisioning and deprovisioning.
                      </span>
                    </div>
                  </li>
                  <li className="flex gap-3" data-id="element-788" data-mp="3iwqpcy25jjq3">
                    <CheckCircle2Icon className="w-5 h-5 text-cyan-700 shrink-0 mt-0.5" strokeWidth={1.75} data-id="element-789" data-mp="3muwq9pgb7533" />
                    <div data-id="element-790" data-mp="393ryf9j6nh3b">
                      <span className="font-medium text-gray-800" data-id="element-791" data-mp="392gsp466fz9k">
                        Role-based access
                      </span>{' '}
                      <span className="text-gray-700" data-id="element-792" data-mp="2z5vlcc6oqc2k">
                        separates coach, school leader, and district admin
                        permissions so each role sees only what it needs.
                      </span>
                    </div>
                  </li>
                  <li className="flex gap-3" data-id="element-793" data-mp="2v4s5hpon0g1c">
                    <CheckCircle2Icon className="w-5 h-5 text-cyan-700 shrink-0 mt-0.5" strokeWidth={1.75} data-id="element-794" data-mp="2v5l8cfkh6le7" />
                    <div data-id="element-795" data-mp="oevhr93dhch5">
                      <span className="font-medium text-gray-800" data-id="element-796" data-mp="ochq7a7wiiec">
                        Least-privilege internal access
                      </span>{' '}
                      <span className="text-gray-700" data-id="element-797" data-mp="13i5bhw01wmln">
                        production access is limited to a small, named
                        engineering group and reviewed on a regular cadence.
                      </span>
                    </div>
                  </li>
                </ul>
              </Card>
            </section>

            {/* Sub-processors */}
            <section id="sub-processors" data-id="element-798" data-mp="1m726y376pl5g">
              <SectionTitle title="Sub-processors" data-id="element-799" data-mp="1m60k71gt3a7w" />
              <Card data-id="element-800" data-mp="1h9a6ulwdysq6">
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-801" data-mp="1h7yzyd7n94uu">
                  Nisa relies on a focused set of service providers to operate
                  the product. We maintain a current list of these
                  sub-processors and the data each one handles.
                </p>
                <div className="flex flex-wrap gap-2 mb-6" data-id="element-802" data-mp="vk0t9lphudw">
                  {subProcessors.map(s => <span key={s} className="px-3 py-1 rounded-full text-sm bg-gray-50 text-gray-700 border border-gray-200" data-id="element-803" data-mp="3sz30j79wrxsk">
                      {s}
                    </span>)}
                </div>
                <div className="bg-gray-50 border border-gray-100 rounded-lg p-4 text-sm text-gray-600" data-id="element-804" data-mp="20vyopflmvyr6">
                  Full sub-processor list available on request to{' '}
                  <a href="mailto:security@nisa.coach" className="text-cyan-700 hover:text-cyan-800 underline underline-offset-2" data-id="element-805" data-mp="2auo7rbp9np90">
                    security@nisa.coach
                  </a>
                  .
                </div>
              </Card>
            </section>

            {/* Penetration Testing */}
            <section id="pen-testing" data-id="element-806" data-mp="2om2p7e3izidy">
              <SectionTitle title="Penetration testing" data-id="element-807" data-mp="2on4d4j5n3zd3" />
              <Card data-id="element-808" data-mp="2tlp37pr6h2nt">
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-809" data-mp="2tk49g2cotnr0">
                  Independent penetration testing is part of our security
                  roadmap. We will publish a summary attestation here once the
                  current assessment cycle completes.
                </p>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-sm" data-id="element-810" data-mp="qztkt1kslisq">
                  <ClockIcon className="w-4 h-4" strokeWidth={1.75} data-id="element-811" data-mp="hy2djsalt3wr" />
                  Details to be added once assessment completes
                </div>
              </Card>
            </section>

            {/* Incident Response */}
            <section id="incident" data-id="element-812" data-mp="1an0y0w278i58">
              <SectionTitle title="Incident response" data-id="element-813" data-mp="1aly5ykt549nq" />
              <Card data-id="element-814" data-mp="15nejmjamwvuu">
                <div className="space-y-5" data-id="element-815" data-mp="15ozde6p4karn">
                  <div data-id="element-816" data-mp="10wy6z1en1e9j">
                    <h3 className="text-lg font-semibold text-gray-800 mb-1" data-id="element-817" data-mp="10weljaps47qr">
                      Triage SLA
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-818" data-mp="vxbtcquwdw07">
                      We triage suspected security incidents within 24 hours of
                      detection, with an on-call engineer assigned and a working
                      assessment of impact.
                    </p>
                  </div>
                  <div data-id="element-819" data-mp="196ls6xoimozc">
                    <h3 className="text-lg font-semibold text-gray-800 mb-1" data-id="element-820" data-mp="1d64adloyclyh">
                      Customer notification
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-821" data-mp="z7mrq2dwlznf">
                      Customers affected by a confirmed incident are notified
                      via their primary admin contact, with the facts we have at
                      the time and an explicit commitment to follow-up updates
                      as we learn more.
                    </p>
                  </div>
                  <div data-id="element-822" data-mp="1i45so4ek0h8w">
                    <h3 className="text-lg font-semibold text-gray-800 mb-1" data-id="element-823" data-mp="1i4xhmwdnbgq7">
                      Material incident reporting
                    </h3>
                    <p className="text-gray-700 leading-relaxed" data-id="element-824" data-mp="2atcw9hsv7x10">
                      Material incidents involving confirmed
                      unauthorized access to customer data are reported to
                      affected customers within 72 hours of confirmation.
                    </p>
                  </div>
                </div>
              </Card>
            </section>

            {/* Vulnerability Disclosure */}
            <section id="disclosure" data-id="element-825" data-mp="cxeetcjf62ln">
              <SectionTitle title="Vulnerability disclosure" data-id="element-826" data-mp="8wazcr5aa8ky" />
              <Card data-id="element-827" data-mp="8wugw6r0uuyg">
                <p className="text-gray-700 leading-relaxed mb-4" data-id="element-828" data-mp="8yfbfwdc6dwb">
                  We welcome reports from security researchers, customers, and
                  the broader community. We ask researchers to report suspected
                  issues privately to{' '}
                  <a href="mailto:security@nisa.coach" className="text-cyan-700 hover:text-cyan-800 underline underline-offset-2" data-id="element-829" data-mp="3qdlwcpgxpnr8">
                    security@nisa.coach
                  </a>{' '}
                  with steps to reproduce, and to give us a reasonable window to
                  fix the issue before public disclosure.
                </p>
                <p className="text-gray-700 leading-relaxed" data-id="element-830" data-mp="3ufqi5mb9la1q">
                  In return, we commit to an initial response within 7 business
                  days, status updates at least every 14 days while a report is
                  open, and a coordinated public disclosure timeline agreed with
                  the reporter. Researchers who act in good faith under this
                  policy are covered by our safe-harbor language.
                </p>
              </Card>
            </section>

            {/* Documents & Resources */}
            <section id="documents" data-id="element-831" data-mp="3fr3riog6svv7">
              <SectionTitle title="Documents & resources" data-id="element-832" data-mp="3kpnjb5hgp1vh" />
              <div className="grid md:grid-cols-2 gap-4" data-id="element-833" data-mp="3ko42ndrw8ot6">
                {documents.map(doc => <a key={doc.title} href={doc.href} target={doc.external ? '_blank' : undefined} rel={doc.external ? 'noopener noreferrer' : undefined} className="group bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100 shadow-sm p-6 hover:border-cyan-300 hover:shadow-md transition-all" data-id="element-834" data-mp="3phf75898gthx">
                    <div className="flex items-start justify-between gap-4" data-id="element-835" data-mp="2pf8tauv6y6v2">
                      <div data-id="element-836" data-mp="2kh79u8tus5j2">
                        <div className="font-medium text-gray-800 mb-1 group-hover:text-cyan-700 transition-colors" data-id="element-837" data-mp="2ki08snmklqqn">
                          {doc.title}
                        </div>
                        <div className="text-sm text-gray-600 leading-relaxed" data-id="element-838" data-mp="2bfr1e6mlmdhh">
                          {doc.description}
                        </div>
                      </div>
                      <ExternalLinkIcon className="w-4 h-4 text-gray-400 group-hover:text-cyan-700 shrink-0 mt-1 transition-colors" strokeWidth={1.75} data-id="element-839" data-mp="21qha2szkwyuf" />
                    </div>
                  </a>)}
              </div>
            </section>

            {/* Contact */}
            <section id="contact" data-id="element-840" data-mp="1rqyrogzbhl4h">
              <SectionTitle title="Contact" data-id="element-841" data-mp="1nrhjvgwf4bw5" />
              <Card data-id="element-842" data-mp="1ntapqtz7is33">
                <div className="flex flex-col items-center text-center" data-id="element-843" data-mp="1is4v2qlzkuqx">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-cyan-50 text-cyan-700 border border-cyan-100 mb-4" data-id="element-844" data-mp="1iu7we6slaaay">
                    <MailIcon className="w-6 h-6" strokeWidth={1.75} data-id="element-845" data-mp="3bo5txolga7ns" />
                  </div>
                  <p className="text-gray-700 max-w-2xl mb-6 leading-relaxed" data-id="element-846" data-mp="2xoo210cuyea7">
                    Security questionnaires, DPAs, sub-processor lists, and
                    vulnerability reports all reach us at the same address. We
                    route your request to the right person.
                  </p>
                  <a href="mailto:security@nisa.coach" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-primary-700 text-white font-medium hover:bg-primary-800 transition-colors" data-id="element-847" data-mp="2sq3adpbo92xd">
                    <MailIcon className="w-4 h-4" strokeWidth={2} data-id="element-848" data-mp="2j09ysobj4nt4" />
                    security@nisa.coach
                  </a>
                </div>
              </Card>
            </section>
          </div>

          <p className="text-sm text-gray-500 mt-12 text-center" data-id="element-849" data-mp="29x8veve1tat5">
            Last updated: May 2026
          </p>
        </div>
      </main>
      <Footer data-id="element-850" data-mp="1qelczlh8m7tq" />
    </div>;
};